import { z } from 'zod';
// =============================================================================
// Reliable Message Queue Protocol
// =============================================================================
/**
 * Wrapper for messages that require reliable delivery
 */
export const ReliableMessage = z.object({
    /** Unique message ID for tracking */
    id: z.string(),
    /** Ordering sequence number */
    sequence: z.number().int().nonnegative(),
    /** When the message was originally sent */
    timestamp: z.number(),
    /** The actual message type */
    messageType: z.string(),
    /** The message payload */
    payload: z.unknown(),
    /** Whether this message requires acknowledgment */
    requiresAck: z.boolean().default(true),
});
/**
 * Request to resend missed messages
 */
export const ResendRequest = z.object({
    type: z.literal('RESEND_REQUEST'),
    /** Sequence numbers of missing messages */
    missingSequences: z.array(z.number().int().nonnegative()),
});
// =============================================================================
// Message Queue Implementation
// =============================================================================
let messageIdCounter = 0;
/**
 * Generate a unique message ID
 */
export function generateMessageId() {
    return `msg_${Date.now()}_${++messageIdCounter}_${Math.random().toString(36).slice(2, 8)}`;
}
/**
 * Client-side message queue for reliable delivery
 */
export class MessageQueue {
    pending = new Map();
    sequence = 0;
    config;
    retryTimer = null;
    transmit;
    constructor(transmit, config = {}) {
        this.transmit = transmit;
        this.config = {
            initialRetryDelay: config.initialRetryDelay ?? 1000,
            maxRetryDelay: config.maxRetryDelay ?? 8000,
            retryMultiplier: config.retryMultiplier ?? 2,
            maxRetries: config.maxRetries ?? 3,
            staleTimeout: config.staleTimeout ?? 30000,
            onDeliveryFailed: config.onDeliveryFailed ?? (() => { }),
            onDeliveryConfirmed: config.onDeliveryConfirmed ?? (() => { }),
        };
    }
    /**
     * Send a message with optional reliability
     */
    send(messageType, payload, options = {}) {
        const reliable = options.reliable ?? this.isReliableMessageType(messageType);
        if (reliable) {
            const wrapped = this.wrapMessage(messageType, payload);
            this.pending.set(wrapped.id, {
                message: wrapped,
                retryCount: 0,
                nextRetryAt: Date.now() + this.config.initialRetryDelay,
                lastSentAt: Date.now(),
            });
            this.transmit(wrapped);
            this.startRetryTimer();
            return wrapped.id;
        }
        else {
            // Send directly without wrapping
            const payloadObj = payload && typeof payload === 'object' ? payload : {};
            this.transmit({ type: messageType, ...payloadObj });
            return '';
        }
    }
    /**
     * Wrap a message for reliable delivery
     */
    wrapMessage(messageType, payload) {
        return {
            id: generateMessageId(),
            sequence: this.sequence++,
            timestamp: Date.now(),
            messageType,
            payload,
            requiresAck: true,
        };
    }
    /**
     * Determine if a message type requires reliable delivery by default
     */
    isReliableMessageType(messageType) {
        const reliableTypes = [
            'JOIN_ROOM',
            'LEAVE_ROOM',
            'PLAYER_INPUT',
            'START_GAME',
            'SELECT_GAME',
            'KICK_PLAYER',
            'CLOSE_ROOM',
            'RETURN_TO_LOBBY',
            'UPDATE_AVATAR',
        ];
        return reliableTypes.includes(messageType);
    }
    /**
     * Acknowledge a message as received
     */
    acknowledge(messageId) {
        const pending = this.pending.get(messageId);
        if (pending) {
            this.pending.delete(messageId);
            this.config.onDeliveryConfirmed(pending.message);
        }
        // Stop timer if no more pending messages
        if (this.pending.size === 0) {
            this.stopRetryTimer();
        }
    }
    /**
     * Acknowledge multiple messages
     */
    acknowledgeBatch(messageIds) {
        for (const id of messageIds) {
            this.acknowledge(id);
        }
    }
    /**
     * Handle reconnection - resend all pending messages
     */
    onReconnect() {
        const now = Date.now();
        for (const [id, pending] of this.pending.entries()) {
            // Skip stale messages
            if (now - pending.message.timestamp > this.config.staleTimeout) {
                this.pending.delete(id);
                this.config.onDeliveryFailed(pending.message, 'Message timed out');
                continue;
            }
            // Resend
            pending.lastSentAt = now;
            pending.nextRetryAt = now + this.config.initialRetryDelay;
            this.transmit(pending.message);
        }
        if (this.pending.size > 0) {
            this.startRetryTimer();
        }
    }
    /**
     * Start the retry timer
     */
    startRetryTimer() {
        if (this.retryTimer)
            return;
        this.retryTimer = setInterval(() => {
            this.processRetries();
        }, 500);
    }
    /**
     * Stop the retry timer
     */
    stopRetryTimer() {
        if (this.retryTimer) {
            clearInterval(this.retryTimer);
            this.retryTimer = null;
        }
    }
    /**
     * Process pending messages that need retrying
     */
    processRetries() {
        const now = Date.now();
        const toRemove = [];
        for (const [id, pending] of this.pending.entries()) {
            // Check for stale messages
            if (now - pending.message.timestamp > this.config.staleTimeout) {
                toRemove.push(id);
                this.config.onDeliveryFailed(pending.message, 'Message timed out');
                continue;
            }
            // Check if retry is due
            if (now >= pending.nextRetryAt) {
                if (pending.retryCount >= this.config.maxRetries) {
                    toRemove.push(id);
                    this.config.onDeliveryFailed(pending.message, 'Max retries exceeded');
                    continue;
                }
                // Retry with exponential backoff
                const delay = Math.min(this.config.initialRetryDelay * Math.pow(this.config.retryMultiplier, pending.retryCount), this.config.maxRetryDelay);
                pending.retryCount++;
                pending.lastSentAt = now;
                pending.nextRetryAt = now + delay;
                this.transmit(pending.message);
            }
        }
        // Clean up failed messages
        for (const id of toRemove) {
            this.pending.delete(id);
        }
        // Stop timer if no more pending
        if (this.pending.size === 0) {
            this.stopRetryTimer();
        }
    }
    /**
     * Get count of pending messages
     */
    getPendingCount() {
        return this.pending.size;
    }
    /**
     * Get all pending messages (for debugging)
     */
    getPendingMessages() {
        return Array.from(this.pending.values()).map((p) => p.message);
    }
    /**
     * Clear all pending messages
     */
    clear() {
        this.pending.clear();
        this.stopRetryTimer();
    }
    /**
     * Clean up resources
     */
    dispose() {
        this.stopRetryTimer();
        this.pending.clear();
    }
}
/**
 * Server-side tracker for deduplication and ordering
 */
export class MessageTracker {
    received = new Map();
    highestSequence = -1;
    missingSequences = new Set();
    /**
     * Track a received message
     * @returns true if message is new, false if duplicate
     */
    receive(message) {
        // Check for duplicate
        if (this.received.has(message.id)) {
            return false;
        }
        // Track the message
        this.received.set(message.id, {
            sequence: message.sequence,
            receivedAt: Date.now(),
        });
        // Update sequence tracking
        if (message.sequence > this.highestSequence + 1) {
            // We have a gap - track missing sequences
            for (let i = this.highestSequence + 1; i < message.sequence; i++) {
                this.missingSequences.add(i);
            }
        }
        else if (this.missingSequences.has(message.sequence)) {
            // Fill in a gap
            this.missingSequences.delete(message.sequence);
        }
        if (message.sequence > this.highestSequence) {
            this.highestSequence = message.sequence;
        }
        return true;
    }
    /**
     * Get missing sequence numbers for resend request
     */
    getMissingSequences() {
        return Array.from(this.missingSequences).sort((a, b) => a - b);
    }
    /**
     * Get the highest received sequence
     */
    getHighestSequence() {
        return this.highestSequence;
    }
    /**
     * Clean up old entries (call periodically)
     */
    cleanup(maxAge = 60000) {
        const cutoff = Date.now() - maxAge;
        for (const [id, info] of this.received.entries()) {
            if (info.receivedAt < cutoff) {
                this.received.delete(id);
            }
        }
    }
    /**
     * Reset the tracker
     */
    reset() {
        this.received.clear();
        this.highestSequence = -1;
        this.missingSequences.clear();
    }
}
//# sourceMappingURL=queue.js.map